import { useState, CSSProperties } from 'react';
import { Menu, MenuItem } from '@szhsin/react-menu';
import cx from 'classnames';

import useAppSeasons from 'hooks/useAppSeasons';
import useBreakpointValue from 'hooks/useBreakpointValue';
import { MarketTopSalesResponse, useMarketTopSales } from 'api/market';

import Spinner from 'shared_components/Spinner';
import Text from 'shared_components/Text';
import TopSalesItem from './TopSalesItem';
import { ChevronDownIcon, FireIcon } from 'shared_components/SVGIcons';

import styles from './styles.module.scss';

type TopSale = MarketTopSalesResponse['top'][number];

const topSaleRanges: Readonly<MarketReportSalesRange[]> = [
  'day',
  'week',
  'month',
  'alltime',
];

let topSalesHasReturnedEmpty = false;

const defaultTopSalesRange: MarketReportSalesRange = topSalesHasReturnedEmpty
  ? 'month'
  : 'day';

type Props = {
  className?: string;
  defaultRange?: 'day' | 'week' | 'month' | 'alltime';
  headerClassName?: string;
  style?: CSSProperties;
};

const TopSalesBlock = (props: Props) => {
  const { className, defaultRange, headerClassName, style } = props;
  const { season } = useAppSeasons();
  const [
    saleReportsRange,
    setSaleReportsRange,
  ] = useState<MarketReportSalesRange>(defaultRange || defaultTopSalesRange);

  const { data: sales } = useMarketTopSales(
    false,
    {
      range: saleReportsRange,
      season,
    },
    {
      onSuccess: (response) => {
        if (saleReportsRange === 'day' && !topSalesHasReturnedEmpty) {
          if (response?.data?.top) {
            if (response.data.top.length === 0) {
              topSalesHasReturnedEmpty = true;
              setSaleReportsRange('week');
            }
          }
        }
      },
    }
  );

  const cardColumnsCount = useBreakpointValue({
    1500: 7,
    1080: 5,
    740: 3,
    480: 3,
    0: 2,
  });

  return (
    <div className={className} style={style}>
      <Text
        block
        as="h2"
        type="white"
        size="base"
        uppercase
        weight="semibold"
        className={cx(headerClassName, styles.mainTitle)}
      >
        <FireIcon size={20} className={styles.icon} /> Top Sales{' '}
        <Menu
          className={styles.menuDropDown}
          menuButton={
            <button className={styles.collectorsRangeBtn}>
              <Text
                as="h2"
                type="primary"
                underlined
                size="md"
                weight="semibold"
                capitalize
                className={styles.rangeText}
              >
                {formatRangeTitle(saleReportsRange)}
              </Text>
              <ChevronDownIcon size={8} />
            </button>
          }
          offsetX={6}
          direction="bottom"
        >
          {topSaleRanges.map((r) => (
            <MenuItem onClick={() => setSaleReportsRange(r)} key={r}>
              {formatRangeTitle(r)}
            </MenuItem>
          ))}
        </Menu>
      </Text>

      <div className={styles.cardSectionWrapper}>
        {sales?.top.length ? (
          <div className={styles.boxCards}>
            {sales.top.slice(0, cardColumnsCount).map(mapSales)}
          </div>
        ) : sales?.top.length === 0 ? (
          <div className={styles.emptyContent}>
            <Text size="sm" type="muted" weight="medium">
              No cards found.
            </Text>
          </div>
        ) : null}
        {!sales && <Spinner className={styles.centeredSpinnerBlock} />}
      </div>
    </div>
  );
};

const formatRangeTitle = (
  x: MarketReportUsersRange | MarketReportSalesRange
) => {
  if (x === 'day') return 'Today';
  if (x === 'week') return 'This Week';
  if (x === 'month') return 'This Month';
  if (x === 'alltime') return 'All Time';
};

const mapSales = (entry: TopSale, idx: number) => (
  <TopSalesItem {...entry} key={idx} />
);

export default TopSalesBlock;
